import { Autocomplete, TextField } from '@mui/material';
import { MALAYSIAN_BANKS, bankGroupOf } from '../../utils/malaysianBanks';

/**
 * One bank picker, used everywhere a payee's bank is entered: both admin user
 * screens, an agent's own profile, the public sign-up, the new-referrer dialog
 * and the referrer page. Six boxes that were six independent free-text fields.
 * (user 2026-10-22: "can the bank go for dropdown for all?" /
 *  "so bank next time all put as select")
 *
 * PICK ONLY — no free typing. Typing is what produced 35 spellings for about 15
 * banks: Maybank five ways, "publick bank", CIMB and RHB each twice differing
 * only by a trailing space, and five rows holding a person's name instead of a
 * bank. You can still TYPE TO SEARCH — "may" reaches Maybank in three
 * keystrokes — but what gets stored is always an entry from the list.
 *
 * With one exception, and it matters: whatever is already on the record stays
 * selectable. A strict list would otherwise blank an off-list value the moment
 * somebody opened the form and saved it, quietly wiping a bank off a payout.
 * So the current value is added to the options when it is not a listed bank,
 * shown under "Currently on record" — visible, keepable, and replaceable the
 * moment anyone picks a real bank.
 *
 * If a bank is genuinely missing from the list, it belongs in
 * utils/malaysianBanks.js, not typed into one person's record.
 */
export default function BankSelect({
    value,
    onChange,
    label = 'Bank Name',
    required = false,
    size = 'small',
    fullWidth = true,
    sx,
    helperText,
    disabled = false,
}) {
    const current = (value || '').trim();
    const offList = current && !MALAYSIAN_BANKS.includes(current);
    const options = offList ? [...MALAYSIAN_BANKS, current] : MALAYSIAN_BANKS;

    return (
        <Autocomplete
            autoHighlight
            disabled={disabled}
            options={options}
            groupBy={(opt) => (opt === current && offList ? 'Currently on record' : bankGroupOf(opt))}
            value={current || null}
            onChange={(_e, picked) => onChange(picked || '')}
            isOptionEqualToValue={(opt, val) => opt === val}
            size={size}
            fullWidth={fullWidth}
            sx={sx}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    placeholder="Type to search…"
                    helperText={offList ? 'Not a listed bank — pick one to correct it.' : helperText}
                    size={size}
                />
            )}
        />
    );
}
